import React, {useEffect, useState, Fragment} from 'react';
import {useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {CustomDialog, MemberCard, ModuleForm, ProgressBar} from "components";
import {setMemberDialog} from "redux/actions";
//Material UI Components
import {
    Button,
    Card,
    CardHeader,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    makeStyles,
    OutlinedInput,
    Select,
    Typography,
    useMediaQuery,
    useTheme
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search"
import {useFetchMemberOptions, useFetchMembers} from "hooks";

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: `0.25em 0`,
        [theme.breakpoints.down('xs')]: {
            margin: '0.5em 0'
        }
    },
    headerRoot: {
        padding: theme.spacing(1),
    },
    cardTitle: {
        ...theme.typography.body2,
        color: theme.palette.common.black
    },
    cardSubtitle: {
        '&:root': {
            padding: 0
        },
        padding: 0,
        color: theme.palette.common.black
    },
    cardContainer: {
        border: '1px solid rgba(0, 0, 0, 0.25)',
        borderRadius: '5px',
        margin: '0.5em 0',
    }
}));

const MemberDialog = ({open, setMemberRecords}) => {
    //Hooks
    const theme = useTheme();
    const classes = useStyles();
    const dispatch = useDispatch();
    const {register, handleSubmit} = useForm();
    const [memberOptions] = useFetchMemberOptions()
    const matchesXS = useMediaQuery(theme.breakpoints.down('sm'))
    const [fetchMembers, members, fetchingMembers, membersFetched] = useFetchMembers()

    const {composeDialog} = useSelector(state => state.inbox)
    const [checkedMembers, setCheckedMembers] = useState([]);

    const handleClose = () => dispatch(setMemberDialog(false));

    const handleToggleAll = () => {
        if (members?.length === checkedMembers?.length)
            setCheckedMembers([]);
        else
            setCheckedMembers(members.map(row => row.id));
    };

    const onSubmit = (data) => fetchMembers(data);

    const handleConfirm = () => {
        setMemberRecords(members.filter(member => checkedMembers.includes(member.id) && member));
        handleClose();
    };

    useEffect(() => composeDialog && fetchMembers(), [composeDialog, fetchMembers])

    return (
        <Fragment>
            <CustomDialog dialogStatus={open} fullScreen={matchesXS} fullWidth title='Members' handleClose={handleClose}
                          maxWidth='md'>
                <DialogContent>
                    <Grid container direction="column">
                        {/*================    Members Filtering Inputs   ================*/}
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/*==========   Company/Member input    ==========*/}
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined" className={classes.formControl}>
                                    <InputLabel htmlFor="company members">Company/Member</InputLabel>
                                    <OutlinedInput name="name" label="Company/Member" placeholder="Company/Member"
                                                   inputRef={register}
                                                   endAdornment={
                                                       <InputAdornment position="end">
                                                           <IconButton onClick={handleSubmit(onSubmit)} edge="end">
                                                               <SearchIcon/>
                                                           </IconButton>
                                                       </InputAdornment>
                                                   }
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container className='space-2'>
                                    {   /*================      Member Type Options     ================*/}
                                    <Grid item sm={4} xs={12}>
                                        <FormControl fullWidth
                                                     variant="outlined"
                                                     className={classes.formControl}>
                                            <InputLabel htmlFor="member type">
                                                Member Type
                                            </InputLabel>
                                            <Select
                                                native
                                                name="member_type"
                                                label="Member type"
                                                onChange={handleSubmit(onSubmit)}
                                                inputRef={register}
                                            >
                                                {/*TODO:: Set the backend for All */}
                                                <option value=""/>
                                                <option value="all">All</option>
                                                {memberOptions && Object.values(memberOptions.member_type)
                                                    .map((menu, i) =>
                                                        <option key={i} value={menu}>{menu}</option>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {   /*================      Industries Options        ================*/
                                        memberOptions?.modules.includes('Industries') &&
                                        (<ModuleForm label={"Industries"} module={memberOptions?.industries}
                                                     name={"industry_id"} handleChange={handleSubmit(onSubmit)}
                                                     register={register}/>)
                                    }
                                    {   /*================      Services Options        ================*/
                                        memberOptions?.modules.includes('Services') &&
                                        (<ModuleForm label={"Services"} module={memberOptions?.services}
                                                     name={"service_id"} handleChange={handleSubmit(onSubmit)}
                                                     register={register}/>)
                                    }
                                    {   /*================      Sectors Options        ================*/
                                        memberOptions?.modules.includes('Sectors') &&
                                        (<ModuleForm label={"Sectors"} module={memberOptions?.sectors}
                                                     name={"sector_id"} handleChange={handleSubmit(onSubmit)}
                                                     register={register}/>)
                                    }
                                    {   /*================      Products Options       ================*/
                                        memberOptions?.modules.includes('Products') &&
                                        <ModuleForm label={"Products"} module={memberOptions?.products}
                                                    name={"product_id"} handleChange={handleSubmit(onSubmit)}
                                                    register={register}/>
                                    }
                                    {   /*================      Technologies Options        ================*/
                                        memberOptions?.modules.includes('Technologies') &&
                                        <ModuleForm label={"Technologies"} module={memberOptions?.technologies}
                                                    name={"technology_id"} handleChange={handleSubmit(onSubmit)}
                                                    register={register}/>
                                    }
                                </Grid>
                            </Grid>

                            {members?.length > 0 && (
                                <Grid item xs={12} className={classes.cardContainer}>
                                    <Card elevation={0} style={{width: '100%'}}>
                                        <CardHeader
                                            classes={{
                                                subheader: classes.cardSubtitle,
                                                title: classes.cardTitle,
                                                root: classes.headerRoot
                                            }}
                                            avatar={
                                                <Checkbox
                                                    color="primary"
                                                    onClick={handleToggleAll}
                                                    checked={members?.length === checkedMembers?.length}
                                                />
                                            }
                                            title="Choose All Member"
                                            subheader={` ${checkedMembers?.length}/ ${members?.length}  selected`}
                                        />
                                    </Card>
                                </Grid>
                            )}
                            <Grid item style={{overflow: 'hidden'}}>
                                {(() => {
                                    if (fetchingMembers) {
                                        return <div>
                                            <ProgressBar/>
                                        </div>
                                    }
                                    if (members) {
                                        if (members?.length === 0 && !membersFetched) {
                                            return <Typography align='center'
                                                               variant='h6'> Please Fill at least one Criteria
                                            </Typography>
                                        } else if (membersFetched && members?.length === 0) {
                                            return <Typography align='center'
                                                               variant='h6'> No Record Found
                                            </Typography>
                                        } else {
                                            return <MemberCard members={members}
                                                               checkedMembers={checkedMembers}
                                                               setCheckedMembers={setCheckedMembers}/>
                                        }
                                    } else {
                                        return <Typography align='center'
                                                           variant='h6'> Please Fill the Criteria
                                        </Typography>
                                    }
                                })()}
                            </Grid>
                        </form>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirm} color="primary" variant="outlined">
                        Confirm
                    </Button>
                </DialogActions>
            </CustomDialog>
        </Fragment>
    )
};

export default MemberDialog;
